import { Link } from "@tanstack/react-router";

import { Button } from "~/components/ui/button";
import { cn } from "~/lib/cn";
import {
  type ConsentGroups,
  FULL_CONSENT,
  NO_CONSENT,
} from "~/lib/shared/consent";

/**
 * The first-visit notice in opt-in regions: a card in a bottom corner, a
 * bottom sheet on phones. It neither takes focus nor blocks the page.
 * Rejecting is as easy as accepting, so both buttons share one size and look.
 *
 * It sits above the pages' sticky bars (z-40) and below dialogs and menus
 * (z-50). The signed-in shells keep the account menu in the sidebar's
 * bottom-left corner, so there the card takes the bottom-right one.
 */
export function CookieBanner({
  corner,
  pending,
  onChoose,
  onManage,
}: {
  readonly corner: "left" | "right";
  readonly pending: boolean;
  readonly onChoose: (groups: ConsentGroups) => void;
  readonly onManage: () => void;
}): React.JSX.Element {
  return (
    <section
      aria-label="Cookie notice"
      className={cn(
        "fixed inset-x-0 bottom-0 z-45 rounded-t-2xl border border-gray-200 bg-white p-4 shadow-xl sm:inset-x-auto sm:bottom-6 sm:w-full sm:max-w-md sm:rounded-2xl sm:p-6",
        corner === "left" ? "sm:left-6" : "sm:right-6",
      )}
    >
      <h2 className="font-heading text-lg font-medium text-gray-900">
        Your cookie choices
      </h2>
      <p className="mt-2 text-sm text-gray-700">
        We use cookies and similar tools to run this site. With your OK, we also
        use analytics tools to see what works and advertising tools to measure
        our ads. You can change your mind at any time in Cookie settings.
      </p>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button disabled={pending} onClick={() => onChoose(NO_CONSENT)}>
          Reject all
        </Button>
        <Button disabled={pending} onClick={() => onChoose(FULL_CONSENT)}>
          Accept all
        </Button>
      </div>
      <div className="mt-2 flex items-center justify-between text-sm">
        <Button
          variant="link"
          className="px-0 text-purple-700 underline underline-offset-2"
          onClick={onManage}
        >
          Manage choices
        </Button>
        <Link
          to="/page/$name"
          params={{ name: "privacy-policy" }}
          className="text-purple-700 underline underline-offset-2 hover:text-purple-900"
        >
          Privacy Policy
        </Link>
      </div>
    </section>
  );
}
